const ACTIVE = new Set(['queued', 'running', 'retrying']);
const TERMINAL = new Set(['done', 'partial', 'error', 'cancelled']);

export function isActiveIntegrationJob(job) {
    return ACTIVE.has(job?.status);
}

export function isTerminalIntegrationJob(job) {
    return TERMINAL.has(job?.status);
}

export function isSuccessfulIntegrationJob(job) {
    return job?.status === 'done' && !job.error && !(job.summary?.errors?.length) && !(job.audit?.errors?.length);
}

export function integrationJobLabel(job) {
    if (job?.cancelRequested && isActiveIntegrationJob(job)) return 'Cancellation requested';
    return ({ queued: 'Queued', running: 'Running', retrying: 'Waiting to retry', done: 'Complete', partial: 'Finished with issues', error: 'Failed', cancelled: 'Cancelled' })[job?.status] || 'Checking saved progress';
}

export function unwrapIntegrationJob(data) {
    return data?.job || data;
}

export function jobReadyForWork(job, now = Date.now()) {
    return isActiveIntegrationJob(job) && (!job.nextRetryAt || new Date(job.nextRetryAt).getTime() <= now);
}

export function jobPollFailure(error, failures, now = Date.now()) {
    const status = error?.response?.status;
    if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
        const message = status === 404
            ? 'Saved job was not found. Automatic status checks have stopped.'
            : status === 401 || status === 403
                ? 'Access to this job was denied. Sign in with the account that started it.'
                : error.response?.data?.message || 'Status checks have stopped. Check this job again before starting another run.';
        return { stop: true, message };
    }
    if (failures >= 5) return { stop: true, message: 'Status is temporarily unavailable. Saved work has not been cancelled; use Check saved progress to reconnect.' };
    const retryAfter = error?.response?.headers?.['retry-after'];
    const seconds = Number(retryAfter);
    const headerDelay = retryAfter ? (Number.isFinite(seconds) ? seconds * 1000 : new Date(retryAfter).getTime() - now) : 0;
    return { stop: false, delay: Math.max(Math.min(60000, 2000 * (2 ** (failures - 1))), Number.isFinite(headerDelay) ? Math.max(0, headerDelay) : 0) };
}

/** One request at a time; a stopped observer never changes the persisted job. */
export function createIntegrationJobPoller({ fetchJob, onJob, onStop, onRetry = () => {}, schedule = setTimeout, cancel = clearTimeout, now = Date.now, maxDurationMs = 30 * 60 * 1000 }) {
    let stopped = false;
    let timer;
    let failures = 0;
    const startedAt = now();
    const stop = () => {
        stopped = true;
        if (timer !== undefined) cancel(timer);
    };
    const poll = async () => {
        if (stopped) return;
        if (now() - startedAt >= maxDurationMs) {
            stop();
            onStop('Status checks paused after 30 minutes. Saved work has not been cancelled; use Check saved progress to continue.');
            return;
        }
        try {
            const job = unwrapIntegrationJob(await fetchJob(() => stopped));
            if (stopped) return;
            if (!isActiveIntegrationJob(job) && !isTerminalIntegrationJob(job)) {
                stop();
                onStop('The server did not return a saved job status. Check this job again before starting another run.');
                return;
            }
            failures = 0;
            onJob(job);
            if (isTerminalIntegrationJob(job)) { stop(); return; }
            timer = schedule(poll, 2000);
        } catch (error) {
            if (stopped) return;
            const decision = jobPollFailure(error, ++failures, now());
            if (decision.stop) { stop(); onStop(decision.message); return; }
            onRetry(decision.delay);
            timer = schedule(poll, Math.min(decision.delay, Math.max(0, maxDurationMs - (now() - startedAt))));
        }
    };
    poll();
    return stop;
}
